//Api paths
import { FILTER_FEATURE } from "api/ApiPath";
//Base model class
import { BaseApiResponseModel } from "app/based/baseApiModel/baseApiResponseModel/BaseApiResponseModel";
import { ListBaseApiResponseModel } from "app/based/baseApiModel/baseApiResponseModel/ListBaseApiResponseModel";
import { BaseFilterListRequestModel } from "app/based/baseApiModel/baseFilterListModel/BaseFilterListRequestModel";
import { BaseFilterListResponseModel } from "app/based/baseApiModel/baseFilterListModel/BaseFilterListResponseModel";
import { BaseUserRequestModel } from "app/based/baseApiModel/baseUserModel/BaseUserRequestModel";
import { BaseUserResponseModel } from "app/based/baseApiModel/baseUserModel/BaseUserResponseModel";
//Dependency injections
import { FilterInjection } from "app/filters/di/FilterInjection";
import { IFilterRepository } from "app/filters/domain/repository/IFilterRepository";
import { BalanceHistoryInjection } from "features/balanceHistory/di/BalanceHistoryInjection";
import { injectable } from "tsyringe";
//Utils
import { DateFormat } from "utils/format/DateFormat";
import { paramsChecking } from "utils/helpersInTs/helpersInTs";
//Entities
import { BalanceHistoryActionEntity } from "../entities/BalanceHistoryActionEntity";
import { BalanceHistoryDetailEntity } from "../entities/BalanceHistoryDetailEntity";
import { BalanceHistoryRequestEntity } from "../entities/BalanceHistoryRequestEntity";
import { BalanceHistoryResponseEntity } from "../entities/BalanceHistoryResponseEntity";
import { TransactionForRefundRequestEntity } from "../entities/TransactionForRefundRequestEntity";
import { TransactionForRefundResponseEntity } from "../entities/TransactionForRefundResponseEntity";
//Repository
import { IBalanceHistoryRepository } from "../repositories/IBalanceHistoryRepository";

@injectable()
export class BalanceHistoryUseCase {
  private balanceHistoryRepo: IBalanceHistoryRepository;
  private filterRepo: IFilterRepository;

  constructor() {
    this.balanceHistoryRepo = BalanceHistoryInjection.getBalanceHistoryRepo();
    this.filterRepo = FilterInjection.getRepo();
  }

  private checkParams(value: BalanceHistoryRequestEntity) {
    let params = {
      ...value,
      fromDate: !!value?.created
        ? DateFormat.dateRendering(value?.created, "from")
        : value?.fromDate,
      toDate: !!value?.created
        ? DateFormat.dateRendering(value?.created, "to")
        : value?.toDate,
      code: paramsChecking(value?.code, "input"),
      phoneNumber: paramsChecking(value?.phoneNumber, "input"),
      status: paramsChecking(value?.status, "select"),
      type: paramsChecking(value?.type, "select"),
      applicableType: paramsChecking(value?.applicableType, "select"),
      createdBy: paramsChecking(value?.createdBy, "select"),
      updatedBy: paramsChecking(value?.updatedBy, "select"),
    } as typeof value;
    delete params?.created;
    return params;
  }

  private adjustCreateBody(body: BalanceHistoryActionEntity) {
    const entity = new BalanceHistoryActionEntity(body);
    let createBody = {
      amount: entity.includeAmount() ? body?.amount?.toString() : undefined,
      phone: body?.phone,
      type: body?.type,
      partner: body?.partner,
      provider: body?.provider,
      applicableType: body?.applicableType,
      reason: paramsChecking(body?.reason, "input"),
      transactions: entity.includeTransaction()
        ? body?.transactions
        : undefined,
    };
    return createBody;
  }

  private adjustUpdateBody(body: BalanceHistoryActionEntity) {
    let updateBody = {
      code: body?.code,
      status: body?.status,
      provider: body?.provider,
    };
    return updateBody;
  }

  async getList(
    params: BalanceHistoryRequestEntity,
  ): Promise<ListBaseApiResponseModel<BalanceHistoryResponseEntity>> {
    const updatedParams = this.checkParams(params);
    return await this.balanceHistoryRepo.getList(updatedParams);
  }

  async getDetail(params: {
    code: string;
  }): Promise<BaseApiResponseModel<BalanceHistoryDetailEntity>> {
    return await this.balanceHistoryRepo.getDetail(params);
  }

  async getTransactionForRefund(
    params: TransactionForRefundRequestEntity,
  ): Promise<ListBaseApiResponseModel<TransactionForRefundResponseEntity>> {
    return await this.balanceHistoryRepo.getTransactionForRefund(params);
  }

  async updateTicket(
    body: BalanceHistoryActionEntity,
  ): Promise<BaseApiResponseModel<BalanceHistoryDetailEntity>> {
    const updateBody = this.adjustUpdateBody(body);
    return await this.balanceHistoryRepo.updateTicket(updateBody);
  }

  async createTicket(
    body: BalanceHistoryActionEntity,
  ): Promise<BaseApiResponseModel<BalanceHistoryDetailEntity>> {
    const createBody = this.adjustCreateBody(body);
    return await this.balanceHistoryRepo.createTicket(createBody);
  }

  async getStaffList(params: BaseUserRequestModel) {
    const finalPath = FILTER_FEATURE.STAFF_LIST;
    return await this.filterRepo.getFilterList<
      BaseUserRequestModel,
      BaseUserResponseModel
    >(finalPath, params);
  }

  async getPartnerList(params: BaseFilterListRequestModel) {
    const finalPath = FILTER_FEATURE.PARTNER_LIST;
    return await this.filterRepo.getFilterList<
      typeof params,
      BaseFilterListResponseModel
    >(finalPath, params);
  }
}
